import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { subscribeNewsletter, recordQuoteAbandoned } from "../lib/api";
import { track } from "../lib/analytics";
import { X, Bell, ArrowRight } from "lucide-react";
import { useQuoteModal } from "../lib/quoteModal";
import {
  CTA_INSURANCE,
  CTA_AUTO_LOAN,
  CTA_REFINANCE,
  CTA_WARRANTY,
  CTA_DEFAULT,
} from "../lib/ctaVocabulary";
import { INSURIFY_SAVINGS_SHORT } from "../lib/partnerClaims";

const STORAGE_KEY = "carsavr_exit_intent_shown";

// Iter-82 — Context-aware exit-intent modal. The previous version was a
// generic "free PDF checklist" newsletter capture; the refreshed version:
//   1. Detects the visitor's intent from the current URL path
//   2. Surfaces vertical-specific value-anchored copy
//   3. Primary CTA → one-click QuoteModal open (pre-targeted to vertical)
//   4. Secondary CTA → optional email capture for rate alerts
//
// This converts visitors who are mid-shopping into a high-intent
// QuoteModal session at the moment they're about to leave, rather than
// dropping them into a low-intent newsletter funnel.

// Path → vertical inference. Order matters — most specific first.
//
// Iter-85 — Dollar claims here now mirror the values in the editorial
// data_sources registry (hero.refinance_savings, hero.insurance_savings).
// Keep them in sync with the registry — when the admin updates a number
// in /admin → Data Sources, also bump it here. Adding a methodology
// link clarifies that these are sourced industry medians, not
// CarSavr-specific guarantees.
//
// Iter-185.BI — Per-vertical primary CTAs unified to the user-preferred
// vocabulary set (Compare Insurance Quotes / Find Loan Offers / Lower
// My Payment / Compare Protection Plans) so the exit-intent modal
// speaks the same verb as the homepage hero ZIP-form + every intake.
function inferVertical(pathname) {
  if (/^\/(refinance|auto-loan-refinance)/.test(pathname)) {
    return {
      key: "refinance",
      modalCategory: "refinance",
      eyebrow: "Refi rates · 2-min compare",
      headline: "Before you go — see what a 1-point APR drop saves you.",
      sub: "Industry-median refi candidates ($22k balance, 9.2% → 7.3% APR) save about $1,640 over the life of the loan. Free, soft pull, no impact to credit.",
      primaryCta: CTA_REFINANCE,
    };
  }
  if (/^\/(auto-loans|best-auto-loan|new-car-loans|used-car-loans|first-time-buyer|car-affordability|auto-loan-calculator)/.test(pathname)) {
    return {
      key: "auto-loan",
      modalCategory: "auto-loan-new",
      eyebrow: "Auto loan rates · 2-min compare",
      headline: "Before you go — see your real APR.",
      sub: "Get pre-qualified APRs from 20+ vetted lenders with a single soft credit pull. Won't affect your credit score.",
      primaryCta: CTA_AUTO_LOAN,
    };
  }
  if (/^\/(auto-insurance|insurance|cheapest-insurance)/.test(pathname)) {
    return {
      key: "insurance",
      modalCategory: "insurance",
      eyebrow: "Insurance quotes · 2-min compare",
      headline: "Before you go — see if you're overpaying.",
      sub: `${INSURIFY_SAVINGS_SHORT} Free quotes from top U.S. carriers in about 2 minutes. No phone calls.`,
      primaryCta: CTA_INSURANCE,
    };
  }
  if (/^\/(warranty)/.test(pathname)) {
    return {
      key: "warranty",
      modalCategory: "warranty",
      eyebrow: "Extended warranties · 2-min compare",
      headline: "Before you go — get a real warranty quote.",
      sub: "Skip the F&I office markup. Compare extended warranty plans from our top-rated providers, all without a phone call.",
      primaryCta: CTA_WARRANTY,
    };
  }
  // Default: generic value-anchor on the homepage and content surfaces.
  return {
    key: "general",
    modalCategory: undefined,
    eyebrow: "Free · 2-min compare",
    headline: "Before you go — see what you could save.",
    sub: "Compare auto insurance, loan rates, and refinance offers in about 2 minutes. Free, no spam, soft credit pull options. " + INSURIFY_SAVINGS_SHORT,
    primaryCta: CTA_DEFAULT,
  };
}

export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { open: openQuoteModal } = useQuoteModal();
  const location = useLocation();
  const ctx = useMemo(() => inferVertical(location.pathname), [location.pathname]);

  // Iter-189 Sprint D — Abandoned-quote capture from the exit-intent
  // email field. When the user types a valid email + blurs the input
  // WITHOUT clicking "Notify me", we record an abandoned-quote signal.
  // The backend rate-limits at (email, category) so chatty onBlur
  // events don't double-enqueue. `recordedRef` guards against double
  // calls within a single modal lifetime.
  const recordedRef = useRef(false);

  const maybeRecordAbandon = async () => {
    if (recordedRef.current) return;
    if (done) return;  // user already submitted via "Notify me"
    const trimmed = (email || "").trim();
    // Naïve email validation — backend EmailStr is the source of truth;
    // this just gates the fire-and-forget call.
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return;
    recordedRef.current = true;
    try {
      await recordQuoteAbandoned({
        email: trimmed,
        category: ctx.modalCategory || ctx.key || "insurance",
        source_path: location.pathname,
      });
      track("exit_intent_abandon_captured", {
        vertical: ctx.key,
        source: "exit_intent_blur",
      });
    } catch (_) {
      // Swallow — abandon capture is best-effort, never blocks the UI.
      recordedRef.current = false;
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    const trigger = () => {
      setOpen(true);
      try {
        localStorage.setItem(STORAGE_KEY, "1");
      } catch (_) {
        /* swallow */
      }
      track("exit_intent_shown", { vertical: ctx.key });
    };

    const onMouseLeave = (e) => {
      // Only fire when cursor exits via the top of the viewport
      if (e.clientY <= 0) {
        trigger();
        document.removeEventListener("mouseleave", onMouseLeave);
      }
    };

    // Wait a few seconds before arming to avoid annoying users on landing
    const t = setTimeout(() => {
      document.addEventListener("mouseleave", onMouseLeave);
    }, 8000);

    // Iter-82 — non-prod escape hatch so e2e tests (Playwright) can
    // exercise the modal without needing to dispatch a trusted mouse
    // event. Same vertical/headline/payload as the natural trigger.
    if (process.env.NODE_ENV !== "production") {
      window.__carsavrTriggerExitIntent = trigger;
    }

    return () => {
      clearTimeout(t);
      document.removeEventListener("mouseleave", onMouseLeave);
      if (process.env.NODE_ENV !== "production") {
        try {
          delete window.__carsavrTriggerExitIntent;
        } catch (_) {
          /* swallow */
        }
      }
    };
  }, [ctx.key]);

  const close = () => {
    // Iter-189 Sprint D — Closing the modal is also a "leave" signal;
    // catch the case where the user typed an email but never blurred
    // the field before clicking the X / pressing Escape.
    maybeRecordAbandon();
    setOpen(false);
  };

  const handlePrimary = () => {
    track("exit_intent_primary_click", { vertical: ctx.key });
    close();
    openQuoteModal(ctx.modalCategory);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email");
    setLoading(true);
    track("exit_intent_alert_subscribe", { vertical: ctx.key, source: "exit_intent" });
    try {
      await subscribeNewsletter(email);
      track("lead_magnet_success", { source: "exit_intent", vertical: ctx.key });
      setDone(true);
    } catch (_) {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="exit-intent-modal"
      data-vertical={ctx.key}
      className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/60 backdrop-blur-sm p-4 animate-in fade-in"
    >
      <div className="relative bg-white border border-slate-200 max-w-lg w-full p-8 md:p-10">
        <button
          onClick={close}
          aria-label="Close"
          data-testid="exit-intent-close"
          className="absolute top-4 right-4 w-11 h-11 grid place-items-center hover:bg-slate-100 transition-colors"
        >
          <X className="w-4 h-4 text-slate-600" />
        </button>

        {!done ? (
          <>
            <div className="inline-flex items-center gap-2 bg-[#10B981]/15 px-3 py-1.5 mb-4">
              <span
                aria-hidden
                className="w-1.5 h-1.5 bg-[#10B981] rounded-full"
              />
              <span
                data-testid="exit-intent-eyebrow"
                className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-950"
              >
                {ctx.eyebrow}
              </span>
            </div>
            <h2
              data-testid="exit-intent-headline"
              className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-[1.05] text-slate-950 mb-3"
            >
              {ctx.headline}
            </h2>
            <p
              data-testid="exit-intent-sub"
              className="text-slate-600 leading-relaxed mb-2"
            >
              {ctx.sub}
            </p>
            <p className="text-[10px] text-slate-500 mb-6">
              <a
                href="/methodology"
                className="hover:text-slate-950 underline-offset-2 hover:underline"
              >
                See methodology & data sources →
              </a>
            </p>
            <button
              type="button"
              onClick={handlePrimary}
              data-testid="exit-intent-primary"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0A2540] hover:bg-slate-900 text-white font-bold h-12 px-5 transition-all hover:-translate-y-0.5"
            >
              {ctx.primaryCta}
              <ArrowRight className="w-4 h-4" />
            </button>
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-4 h-4 text-slate-500" />
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  Or — email me when rates drop
                </p>
              </div>
              <form
                onSubmit={submit}
                aria-label="Subscribe to rate-drop alerts by email"
                className="flex flex-col sm:flex-row gap-2"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={maybeRecordAbandon}
                  placeholder="you@email.com"
                  data-testid="exit-intent-email"
                  required
                  // Iter-185.L — add explicit `w-full` so mobile (flex-col
                  // layout) renders the input at full container width
                  // instead of the browser-default `<input>` size (~150px).
                  // The `sm:flex-1` keeps it as a flexible row child on
                  // tablet/desktop where the form is flex-row.
                  className="w-full sm:flex-1 h-11 px-3 bg-white border border-slate-300 focus:border-slate-950 outline-none text-sm"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="exit-intent-submit"
                  className="w-full sm:w-auto h-11 px-4 bg-white border border-slate-300 hover:border-slate-950 text-slate-950 font-bold text-sm transition-colors disabled:opacity-60"
                >
                  {loading ? "…" : "Notify me"}
                </button>
              </form>
              <p className="text-[11px] text-slate-500 mt-3">
                No spam. One-click unsubscribe. We only email when a vetted
                lender beats your current rate.
              </p>
            </div>
          </>
        ) : (
          <>
            <h2 className="font-display font-black text-3xl tracking-tighter text-slate-950 mb-3">
              You&apos;re in.
            </h2>
            <p className="text-slate-600 mb-6">
              We&apos;ll email you the moment we see a rate worth your time. In
              the meantime — see what you could save right now.
            </p>
            <button
              type="button"
              onClick={() => {
                close();
                openQuoteModal(ctx.modalCategory);
              }}
              data-testid="exit-intent-redirect"
              className="inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] font-bold h-12 px-6 transition-all hover:-translate-y-0.5"
            >
              {ctx.primaryCta} →
            </button>
          </>
        )}
      </div>
    </div>
  );
}
